import * as React from "react";
import { Bar, BarChart, LabelList, XAxis, YAxis } from "recharts";

import { cn } from "../../utils/utils";
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
    ChartTooltipFormatter,
} from "../ui/chart";

export interface RankingDatum {
    /** Rótulo da categoria (eixo Y). */
    label: string;
    /** Valor da barra. */
    value: number;
    [key: string]: unknown; // Permite metadados adicionais (como unlocked)
}

interface RankingBarChartProps {
    data: RankingDatum[];
    /** Rótulo + cor da série. Deve conter a chave `value`. */
    config: ChartConfig;
    /** Sufixo do valor no rótulo da barra (ex.: "%"). */
    unit?: string;
    /** Largura reservada para os rótulos do eixo Y. */
    yWidth?: number;
    /** Fixa o teto do eixo (ex.: 100 p/ %). Sem isso, auto-escala ao maior valor. */
    domainMax?: number;
    tooltipFormatter?: ChartTooltipFormatter;
    className?: string;
}

/**
 * Barras horizontais "ranking" padronizadas sobre `ui/chart`: uma barra por
 * categoria, ordenadas de fora (a página ordena), com rótulo de valor à direita
 * e tooltip temático. Cor via `config.value` (token de chart), nunca hardcoded.
 */
export function RankingBarChart({
    data,
    config,
    unit = "",
    yWidth = 150,
    domainMax,
    tooltipFormatter,
    className,
}: RankingBarChartProps) {
    return (
        <ChartContainer
            config={config}
            className={cn("aspect-auto h-full w-full", className)}
        >
            <BarChart
                data={data}
                layout="vertical"
                margin={{ left: 4, right: 36, top: 4, bottom: 4 }}
            >
                <XAxis
                    type="number"
                    hide
                    domain={domainMax ? [0, domainMax] : undefined}
                />
                <YAxis
                    type="category"
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    width={yWidth}
                    tick={{ fontSize: 12 }}
                    interval={0}
                />
                <ChartTooltip
                    cursor={{ fill: "hsl(var(--muted-foreground) / 0.1)" }}
                    content={<ChartTooltipContent indicator="dot" formatter={tooltipFormatter} />}
                />
                <Bar
                    dataKey="value"
                    fill="var(--color-value)"
                    radius={[0, 4, 4, 0]}
                    maxBarSize={26}
                >
                    <LabelList
                        dataKey="value"
                        position="right"
                        offset={8}
                        className="fill-foreground"
                        fontSize={12}
                        formatter={(value: number) => `${value}${unit}`}
                    />
                </Bar>
            </BarChart>
        </ChartContainer>
    );
}
